import { useState, useCallback, useEffect } from 'react';
import { format } from 'date-fns';
import AdminService from '../../Services/AdminService';
import ExpandableComponent from '../../Utils/Expand/expandable-component.js';
import { ButtonBlack } from '../../Utils/Buttons/button-component.js';

const USER_TZ = 'Asia/Jerusalem';

export default function AdminLogs({ t, dir }) {
    const [logs, setLogs] = useState([]);
    const [tail, setTail] = useState(250);
    const [grep, setGrep] = useState('');
    const [inputTail, setInputTail] = useState(250);
    const [inputGrep, setInputGrep] = useState('');
    const [logRange, setLogRange] = useState('1h');
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [selectedDate, setSelectedDate] = useState(() => new Date());

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        const parsedTail = Number.isFinite(Number(inputTail)) && Number(inputTail) >= 0 ? Number(inputTail) : tail;
        if (parsedTail !== tail) setTail(parsedTail);
        if ((inputGrep || '') !== grep) setGrep(inputGrep || '');
        try {
            const response = await AdminService.getLogs(parsedTail, inputGrep || '', selectedDate || new Date());
            const extracted = Array.isArray(response) ? response
                : Array.isArray(response?.data) ? response.data
                    : Array.isArray(response?.logs) ? response.logs : [];
            setLogs(extracted);
            setExpanded(extracted.length > 0);
        } catch {
            setLogs([]);
            setExpanded(false);
        } finally {
            setLoading(false);
        }
    }, [inputTail, inputGrep, selectedDate, tail, grep]);

    useEffect(() => { fetchLogs(); }, [tail, grep, logRange, selectedDate]);

    return (
        <div className={`bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mt-10 ${dir === 'rtl' ? 'text-right' : 'text-left'} flex flex-col items-start`}>
            <h2 className="text-2xl font-bold mb-4">{t.title}</h2>
            <div className="flex flex-wrap gap-4 mb-4">
                <input
                    type="number"
                    value={inputTail}
                    onChange={e => setInputTail(e.target.value)}
                    placeholder={t.placeholders.lines}
                    className="px-3 py-2 rounded border text-black text-sm"
                />
                <input
                    type="text"
                    value={inputGrep}
                    onChange={e => setInputGrep(e.target.value)}
                    placeholder={t.placeholders.filter}
                    className="px-3 py-2 rounded border text-black text-sm"
                />
                <select
                    value={logRange}
                    onChange={e => setLogRange(e.target.value)}
                    className="px-3 py-2 rounded border text-black text-sm"
                >
                    <option value="5m">{t.timeRanges.fiveMin}</option>
                    <option value="1h">{t.timeRanges.oneHour}</option>
                    <option value="4h">{t.timeRanges.fourHours}</option>
                    <option value="1d">{t.timeRanges.allDay}</option>
                </select>
                <input
                    type="date"
                    value={format(selectedDate, 'yyyy-MM-dd')}
                    onChange={e => setSelectedDate(new Date(e.target.value))}
                    className="px-3 py-2 rounded border text-black text-sm"
                />
                <div className='max-w-md'>
                    <ButtonBlack onClick={fetchLogs} className="!py-2 !px-8 !w-auto">
                        {loading ? (
                            <span className="flex justify-center items-center gap-2">
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                </svg>
                                {t.actions.loading}
                            </span>
                        ) : t.actions.refresh}
                    </ButtonBlack>
                </div>
            </div>

            <ExpandableComponent isExpanded={expanded}>
                <div dir="ltr" className="flex w-full">
                    <div className="w-full max-h-[80vh] overflow-y-auto">
                        <pre className="relative text-sm whitespace-pre-wrap text-left w-full">
                            {Array.isArray(logs) && logs.length > 0 ? logs
                                .filter(line => /\[(INFO|DEBUG|ERROR|WARN)]/.test(line))
                                .map((line, idx) => {
                                    let color = 'text-gray-300';
                                    if (line.includes('[ERROR]')) color = 'text-red-500';
                                    else if (line.includes('[WARN]')) color = 'text-yellow-400';
                                    else if (line.includes('[INFO]')) color = 'text-black';
                                    else if (line.includes('[DEBUG]')) color = 'text-primary-400';
                                    return (
                                        <div key={idx} className="group relative w-full">
                                            <p className={`${color} m-0 group-hover:opacity-60 transition duration-150`}>
                                                {(() => {
                                                    const match = line.match(/^\[(.*?)\]\[(.*?)\]\[.*\/([^\/\]]+)] (.+)$/);
                                                    if (match) {
                                                        const [, timestamp, level, , message] = match;
                                                        const d = new Date(timestamp);
                                                        return `[${level}] ${d.toLocaleDateString('he-IL', { timeZone: USER_TZ })} ${d.toLocaleTimeString('he-IL', { timeZone: USER_TZ })} - ${message}`;
                                                    }
                                                    return line;
                                                })()}
                                            </p>
                                        </div>
                                    );
                                }) : <p className="text-gray-400">{t.emptyState}</p>}
                        </pre>
                    </div>
                </div>
            </ExpandableComponent>

            <p className="mt-4 text-sm text-gray-500">
                {t.footerNote} {new Date().toLocaleDateString('he-IL', { timeZone: USER_TZ })}
            </p>
        </div>
    );
}
